import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Search, Check, X, ArrowDownRight, Maximize2, Printer } from "lucide-react";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import KPICard from "../components/KPICard";
import MatrixTable from "../components/MatrixTable";
import MatrixCards from "../components/MatrixCards";
import { getInitials } from "../lib/utils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VARIANTS = {
  zae: {
    endpoint: "/matrix",
    kicker: "Cobertura de implementación",
    heroTitleTop: "Implementación Sistema",
    heroTitleBottom: "ZAE",
    heroTitleAccent: "planta",
    heroSub:
      "Visualiza qué módulos están implementados en cada planta. Filas para plantas, columnas para módulos, una sola mirada para saber dónde estás y qué falta.",
    brandLeft: "Implementación",
    brandRight: "ZAE",
    sectionKicker: "01 — Matriz",
    sectionTitle: "Módulos × Planta",
    sectionDesc:
      "Cada celda indica si un módulo está implementado en la planta. Pasa el cursor para detalles.",
    itemLabel: "Módulos",
    itemLabelSingle: "módulo",
    itemLabelPlural: "módulos",
    footer: "Implementación/ZAE · v1.1 · Dirección Tecnologías Tomza",
    showLeader: true,
    otherRoute: { to: "/TomzaAccion", label: "Tomza en Acción" },
  },
  tomza: {
    endpoint: "/tomza-accion",
    kicker: "Guía Ejecutiva Corporativa · 50 temas",
    heroTitleTop: "Tomza",
    heroTitleBottom: "en",
    heroTitleAccent: "Acción",
    heroSub:
      "Avance de los 50 temas de la Guía Ejecutiva Corporativa en cada planta. 5 categorías, 74 plantas.",
    brandLeft: "Tomza",
    brandRight: "Acción",
    sectionKicker: "01 — Matriz",
    sectionTitle: "Temas × Planta",
    sectionDesc:
      "Cada celda indica el avance de un tema en la planta. Pasa el cursor para detalles.",
    itemLabel: "Temas",
    itemLabelSingle: "tema",
    itemLabelPlural: "temas",
    footer: "Tomza en Acción · v1.0 · Dirección Tecnologías Tomza",
    showLeader: false,
    otherRoute: { to: "/", label: "Implementación ZAE" },
  },
};

export default function MatrixPortal({ variant = "zae" }) {
  const cfg = VARIANTS[variant] || VARIANTS.zae;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState("");
  const [moduleFilter, setModuleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [zoneFilter, setZoneFilter] = useState("all");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const matrixRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const load = async (attempt = 0) => {
      try {
        const res = await axios.get(`${API}${cfg.endpoint}`, { timeout: 15000 });
        if (!cancelled) {
          setData(res.data);
          setError(null);
          setLoading(false);
        }
      } catch (e) {
        if (cancelled) return;
        if (attempt < 2) {
          // Retry con back-off (1s, 2s) por si el backend tarda en arrancar
          setTimeout(() => load(attempt + 1), (attempt + 1) * 1000);
        } else {
          setError(e?.message || "Error al cargar datos");
          setLoading(false);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [cfg.endpoint]);

  const filtered = useMemo(() => {
    if (!data) return null;
    const q = search.trim().toLowerCase();

    let companies = data.companies;
    let modules = data.modules;

    if (q) {
      companies = companies.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          c.industry.toLowerCase().includes(q)
      );
    }

    if (zoneFilter !== "all") {
      companies = companies.filter((c) => c.zone === zoneFilter);
    }

    if (moduleFilter !== "all") {
      modules = modules.filter((m) => m.id === moduleFilter);
    }

    if (statusFilter !== "all") {
      const want = statusFilter === "implemented";
      companies = companies.filter((c) => {
        const row = data.implementations[c.id] || {};
        return modules.some((m) => Boolean(row[m.id]) === want);
      });
    }

    return { ...data, companies, modules };
  }, [data, search, moduleFilter, statusFilter, zoneFilter]);

  const kpis = useMemo(() => {
    if (!data) return null;
    const totalCompanies = data.companies.length;
    const totalModules = data.modules.length;
    let implemented = 0;
    let pending = 0;
    data.companies.forEach((c) => {
      data.modules.forEach((m) => {
        if (data.implementations[c.id]?.[m.id]) implemented++;
        else pending++;
      });
    });
    const total = implemented + pending;
    const completion = total ? Math.round((implemented / total) * 100) : 0;
    return { totalCompanies, totalModules, implemented, pending, completion };
  }, [data]);

  const zoneStats = useMemo(() => {
    if (!data) return [];
    const byZone = {};
    data.companies.forEach((c) => {
      const z = c.zone || "Sin zona";
      if (!byZone[z]) byZone[z] = { zone: z, plants: 0, done: 0, total: 0 };
      byZone[z].plants += 1;
      data.modules.forEach((m) => {
        byZone[z].total += 1;
        if (data.implementations[c.id]?.[m.id]) byZone[z].done += 1;
      });
    });
    return Object.values(byZone)
      .map((s) => ({ ...s, pct: s.total ? Math.round((s.done / s.total) * 100) : 0 }))
      .sort((a, b) => a.zone.localeCompare(b.zone, "es"));
  }, [data]);

  return (
    <TooltipProvider delayDuration={150}>
      <div
        className="min-h-screen bg-white text-neutral-950"
        data-testid="matrix-portal"
      >
        {/* Sticky header */}
        <header
          className="sticky top-0 z-50 border-b border-neutral-200 bg-white/80 backdrop-blur-xl backdrop-saturate-150"
          data-testid="site-header"
        >
          <div className="max-w-[1600px] mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img
                src="/logo-zae.png"
                alt="ZAE"
                className="w-8 h-8 object-contain"
                data-testid="brand-logo"
              />
              <span className="font-display text-lg tracking-tight">
                {cfg.brandLeft}<span className="text-neutral-400">/</span>{cfg.brandRight}
              </span>
            </div>
            <nav className="hidden md:flex items-center gap-8 font-mono-data text-xs uppercase tracking-[0.18em] text-neutral-500">
              <a
                href="#overview"
                className="hover:text-neutral-950 transition-colors"
                data-testid="nav-overview"
              >
                Resumen
              </a>
              <a
                href="#matrix"
                className="hover:text-neutral-950 transition-colors"
                data-testid="nav-matrix"
              >
                Matriz
              </a>
              <a
                href="#legend"
                className="hover:text-neutral-950 transition-colors"
                data-testid="nav-legend"
              >
                Leyenda
              </a>
            </nav>
            <div className="flex items-center gap-2 md:gap-4">
              <a
                href={cfg.otherRoute.to}
                className="inline-flex items-center h-9 font-mono-data text-[10px] md:text-xs uppercase tracking-[0.14em] md:tracking-[0.18em] border border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-100 transition-colors px-2 md:px-3"
                data-testid="nav-switch"
              >
                <span className="hidden sm:inline">Ir a</span>
                <span className="sm:ml-1.5">{cfg.otherRoute.label}</span>
              </a>
              <a
                href="#matrix"
                className="inline-flex items-center gap-2 h-9 font-mono-data text-xs uppercase tracking-[0.18em] bg-neutral-950 text-white px-3 md:px-4 hover:bg-neutral-800 transition-colors"
                data-testid="cta-view-matrix"
              >
                Ver matriz <ArrowDownRight className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>
        </header>

        {/* Hero */}
        <section
          className="relative border-b border-neutral-200 overflow-hidden"
          id="overview"
          data-testid="hero-section"
        >
          <div className="absolute inset-0 bg-grid opacity-60 pointer-events-none" />
          <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-b from-transparent to-white pointer-events-none" />
          <div className="relative max-w-[1600px] mx-auto px-6 md:px-12 py-10 md:py-28">
            <div className="hidden md:flex items-center gap-2 mb-8">
              <span className="font-mono-data text-xs uppercase tracking-[0.2em] text-neutral-500">
                Estado · {new Date().getFullYear()}
              </span>
              <span className="w-1 h-1 bg-neutral-300" />
              <span className="font-mono-data text-xs uppercase tracking-[0.2em] text-neutral-500">
                {cfg.kicker}
              </span>
            </div>
            <h1
              className="font-display text-5xl md:text-7xl lg:text-[88px] leading-[0.95] tracking-tighter max-w-5xl"
              data-testid="hero-title"
            >
              {cfg.heroTitleTop}
              <br />
              {cfg.heroTitleBottom} <span className="text-neutral-400">{cfg.heroTitleAccent}</span>.
            </h1>
            <p
              className="mt-8 max-w-2xl text-base md:text-lg text-neutral-600 leading-relaxed"
              data-testid="hero-subtitle"
            >
              {cfg.heroSub}
            </p>

            {/* KPI Grid */}
            {kpis && (
              <div
                className="mt-8 md:mt-16 grid grid-cols-2 lg:grid-cols-4 gap-px bg-neutral-200 border border-neutral-200"
                data-testid="kpi-grid"
              >
                <KPICard
                  label="Plantas"
                  value={kpis.totalCompanies}
                  hint="Total registradas"
                  testid="kpi-companies"
                />
                <KPICard
                  label={cfg.itemLabel}
                  value={kpis.totalModules}
                  hint="Áreas de implementación"
                  testid="kpi-modules"
                />
                <KPICard
                  label="Cobertura"
                  value={`${kpis.completion}%`}
                  hint={`${kpis.implemented} / ${kpis.implemented + kpis.pending} ${cfg.itemLabelPlural}`}
                  accent="emerald"
                  testid="kpi-coverage"
                />
                <KPICard
                  label="Pendientes"
                  value={
                    <>
                      {kpis.pending}
                      <span className="text-[60%] text-neutral-500 ml-2">
                        / {kpis.implemented + kpis.pending}
                      </span>
                    </>
                  }
                  hint="Por implementar"
                  accent="red"
                  testid="kpi-pending"
                />
              </div>
            )}

            {/* Zone KPI grid */}
            {zoneStats.length > 0 && (
              <div
                className="mt-4 md:mt-6"
                data-testid="zone-kpi-grid-wrapper"
              >
                <div className="font-mono-data font-bold text-[12px] md:text-[13px] uppercase tracking-[0.2em] text-neutral-700 mb-3">
                  Avance por zona
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-7">
                  {zoneStats.map((z) => {
                    const color =
                      z.pct >= 80
                        ? "text-emerald-600"
                        : z.pct >= 50
                        ? "text-amber-500"
                        : "text-red-600";
                    return (
                      <div
                        key={z.zone}
                        className="bg-white px-3 py-2 md:p-4 border border-neutral-200 -ml-px -mt-px transition-all hover:-translate-y-[2px] hover:shadow-sm relative group flex items-center justify-between gap-3 md:block"
                        data-testid={`zone-kpi-${z.zone.toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        <div className="min-w-0 flex items-baseline gap-2 md:block">
                          <div className="font-mono-data font-bold text-[11px] uppercase tracking-[0.16em] text-neutral-900 truncate">
                            {z.zone}
                          </div>
                          <div className="font-mono-data font-normal text-[9px] uppercase tracking-[0.12em] text-neutral-500 md:mt-1 shrink-0">
                            [ {z.plants} {z.plants === 1 ? "planta" : "plantas"} ]
                          </div>
                        </div>
                        <div className={`font-display text-xl md:text-3xl tracking-tighter md:mt-1 ${color}`}>
                          {z.pct}%
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Filters + Matrix */}
        <section
          className="max-w-[1600px] mx-auto px-6 md:px-12 py-8 md:py-20"
          id="matrix"
          data-testid="matrix-section"
        >
          {/* Print-only header */}
          <div className="hidden print:block mb-4" data-testid="print-header">
            <div className="flex items-center gap-3 mb-2">
              <img src="/logo-zae.png" alt="ZAE" style={{ width: 28, height: 28, objectFit: "contain" }} />
              <div className="font-display text-xl tracking-tight">
                Implementación / ZAE — Módulos × Planta
              </div>
            </div>
            <div className="text-xs text-neutral-600">
              {filtered?.companies?.length ?? 0} plantas · {filtered?.modules?.length ?? 0} módulos
              {zoneFilter !== "all" && ` · Zona: ${zoneFilter}`}
              {moduleFilter !== "all" && data?.modules && ` · Módulo: ${data.modules.find((m) => m.id === moduleFilter)?.name || moduleFilter}`}
              {statusFilter !== "all" && ` · Estado: ${statusFilter === "implemented" ? "Implementado" : "Pendiente"}`}
              {search && ` · Búsqueda: "${search}"`}
              {" · "}
              {new Date().toLocaleDateString("es-MX", { day: "2-digit", month: "long", year: "numeric" })}
            </div>
          </div>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-mono-data text-xs uppercase tracking-[0.2em] text-neutral-500 mb-3">
                  {cfg.sectionKicker}
                </div>
                <h2 className="font-display text-3xl md:text-4xl tracking-tight leading-none">
                  {cfg.sectionTitle}
                </h2>
                <p className="mt-3 text-neutral-600 max-w-xl">
                  {cfg.sectionDesc}
                </p>
                {data?.updated_at && (
                  <div
                    className="mt-3 font-mono-data text-[10px] md:text-[11px] uppercase tracking-[0.18em] text-neutral-500"
                    data-testid="last-updated"
                  >
                    Última actualización:{" "}
                    {new Date(data.updated_at).toLocaleDateString("es-MX", {
                      day: "2-digit",
                      month: "long",
                      year: "numeric",
                    })}
                  </div>
                )}
              </div>
              {/* Mobile-only search toggle */}
              <button
                type="button"
                onClick={() => setFiltersOpen((v) => !v)}
                className="md:hidden inline-flex items-center justify-center w-10 h-10 border border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-950 hover:text-white hover:border-neutral-950 transition-colors shrink-0"
                data-testid="filters-toggle"
                aria-label={filtersOpen ? "Cerrar filtros" : "Buscar / filtrar"}
              >
                {filtersOpen ? (
                  <X className="w-4 h-4" />
                ) : (
                  <Search className="w-4 h-4" />
                )}
              </button>
            </div>
            <div
              className={`${filtersOpen ? "flex" : "hidden"} md:flex flex-col sm:flex-row gap-3 w-full md:w-auto`}
              data-testid="filter-bar"
            >
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none" />
                <Input
                  placeholder="Buscar planta…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="rounded-none border-neutral-300 pl-9 w-full sm:w-72 h-10 focus-visible:ring-neutral-950 focus-visible:ring-offset-0"
                  data-testid="search-input"
                />
              </div>
              <Select value={zoneFilter} onValueChange={setZoneFilter}>
                <SelectTrigger
                  className="rounded-none border-neutral-300 w-full sm:w-48 h-10 font-mono-data text-xs uppercase tracking-[0.12em]"
                  data-testid="zone-filter-trigger"
                >
                  <SelectValue placeholder="Zona" />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem
                    value="all"
                    className="font-mono-data text-xs uppercase tracking-[0.12em]"
                  >
                    Todas las zonas
                  </SelectItem>
                  {data?.companies &&
                    Array.from(new Set(data.companies.map((c) => c.zone).filter(Boolean)))
                      .sort()
                      .map((z) => (
                        <SelectItem
                          key={z}
                          value={z}
                          className="font-mono-data text-xs uppercase tracking-[0.12em]"
                          data-testid={`zone-option-${z.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                          {z}
                        </SelectItem>
                      ))}
                </SelectContent>
              </Select>
              <Select value={moduleFilter} onValueChange={setModuleFilter}>
                <SelectTrigger
                  className="rounded-none border-neutral-300 w-full sm:w-48 h-10 font-mono-data text-xs uppercase tracking-[0.12em]"
                  data-testid="module-filter-trigger"
                >
                  <SelectValue placeholder="Módulo" />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem
                    value="all"
                    className="font-mono-data text-xs uppercase tracking-[0.12em]"
                  >
                    Todos los módulos
                  </SelectItem>
                  {data?.modules?.map((m) => (
                    <SelectItem
                      key={m.id}
                      value={m.id}
                      className="font-mono-data text-xs uppercase tracking-[0.12em]"
                      data-testid={`module-option-${m.id}`}
                    >
                      {m.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger
                  className="rounded-none border-neutral-300 w-full sm:w-44 h-10 font-mono-data text-xs uppercase tracking-[0.12em]"
                  data-testid="status-filter-trigger"
                >
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem
                    value="all"
                    className="font-mono-data text-xs uppercase tracking-[0.12em]"
                  >
                    Todos
                  </SelectItem>
                  <SelectItem
                    value="implemented"
                    className="font-mono-data text-xs uppercase tracking-[0.12em]"
                  >
                    Implementado
                  </SelectItem>
                  <SelectItem
                    value="pending"
                    className="font-mono-data text-xs uppercase tracking-[0.12em]"
                  >
                    Pendiente
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Active filter summary */}
          <div
            className="flex flex-wrap items-center justify-between gap-2 mb-6 font-mono-data text-[11px] uppercase tracking-[0.16em] text-neutral-500"
            data-testid="filter-summary"
          >
            <div className="flex flex-wrap items-center gap-2">
              <span>
                {filtered?.companies?.length ?? 0} plantas ·{" "}
                {filtered?.modules?.length ?? 0} {cfg.itemLabelPlural} visibles
              </span>
              {(search || moduleFilter !== "all" || statusFilter !== "all" || zoneFilter !== "all") && (
                <button
                  onClick={() => {
                    setSearch("");
                    setModuleFilter("all");
                    setStatusFilter("all");
                    setZoneFilter("all");
                  }}
                  className="ml-2 inline-flex items-center gap-1 border border-neutral-300 px-2 py-1 hover:bg-neutral-100 transition-colors"
                  data-testid="clear-filters"
                >
                  <X className="w-3 h-3" /> Limpiar
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={() => matrixRef.current?.toggleFullscreen?.()}
              className="inline-flex items-center justify-center md:gap-2 w-10 h-10 md:w-auto md:h-auto md:px-3 md:py-2 border border-neutral-300 bg-white text-[11px] uppercase tracking-[0.18em] text-neutral-700 hover:bg-neutral-950 hover:text-white hover:border-neutral-950 transition-colors"
              data-testid="matrix-fullscreen-toggle"
              aria-label="Pantalla completa"
            >
              <Maximize2 className="w-4 h-4 md:w-3.5 md:h-3.5" />
              <span className="hidden md:inline">Pantalla completa</span>
            </button>
            <button
              type="button"
              onClick={() => window.print()}
              className="inline-flex items-center justify-center md:gap-2 w-10 h-10 md:w-auto md:h-auto md:px-3 md:py-2 border border-neutral-300 bg-white text-[11px] uppercase tracking-[0.18em] text-neutral-700 hover:bg-neutral-950 hover:text-white hover:border-neutral-950 transition-colors"
              data-testid="matrix-print"
              aria-label="Imprimir / Exportar PDF"
            >
              <Printer className="w-4 h-4 md:w-3.5 md:h-3.5" />
              <span className="hidden md:inline">Imprimir / PDF</span>
            </button>
          </div>

          {/* Matrix */}
          {loading && (
            <div
              className="border border-neutral-200 p-12 text-center font-mono-data text-sm uppercase tracking-[0.2em] text-neutral-500"
              data-testid="loading-state"
            >
              Cargando matriz…
            </div>
          )}
          {error && (
            <div
              className="border border-red-500 p-12 text-center font-mono-data text-sm uppercase tracking-[0.2em] text-red-600"
              data-testid="error-state"
            >
              {error}
            </div>
          )}
          {!loading && !error && filtered && (
            <>
              {/* Mobile: card view */}
              <div className="md:hidden">
                <MatrixCards
                  companies={filtered.companies}
                  modules={filtered.modules}
                  implementations={filtered.implementations}
                  commitments={data.commitments || {}}
                  defaultCommitment={data.default_commitment || "Jun 2026"}
                  showLeader={cfg.showLeader}
                  progress={data.progress || {}}
                />
              </div>
              {/* Matrix table: hidden on mobile via internal class, visible md+ and during fullscreen */}
              <MatrixTable
                ref={matrixRef}
                companies={filtered.companies}
                modules={filtered.modules}
                implementations={filtered.implementations}
                allModules={data.modules}
                commitments={data.commitments || {}}
                defaultCommitment={data.default_commitment || "Jun 2026"}
                showLeader={cfg.showLeader}
                progress={data.progress || {}}
              />
            </>
          )}
        </section>

        {/* Legend */}
        <section
          className="border-t border-neutral-200 bg-neutral-50"
          id="legend"
          data-testid="legend-section"
        >
          <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-16 grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <div className="font-mono-data text-xs uppercase tracking-[0.2em] text-neutral-500 mb-3">
                02 — Leyenda
              </div>
              <h3 className="font-display text-2xl tracking-tight">
                Cómo leer la matriz
              </h3>
              <p className="mt-3 text-neutral-600 max-w-md">
                Dos formas, dos estados. Sin ambigüedad. Cada cruce dice una
                cosa y solo una.
              </p>
            </div>
            <div className="bg-white border border-neutral-200 p-6 flex items-start gap-4">
              <div className="w-6 h-6 bg-emerald-500 rounded-full grid place-items-center shrink-0 mt-0.5">
                <Check
                  className="w-3.5 h-3.5 text-white"
                  strokeWidth={3}
                />
              </div>
              <div>
                <div className="font-mono-data text-[11px] uppercase tracking-[0.2em] text-neutral-500">
                  Estado A
                </div>
                <div className="font-display text-xl tracking-tight mt-1">
                  Implementado
                </div>
                <p className="text-sm text-neutral-600 mt-2">
                  El módulo está activo y en producción para la planta.
                </p>
              </div>
            </div>
            <div className="bg-white border border-neutral-200 p-6 flex items-start gap-4">
              <div className="w-6 h-6 bg-red-500 grid place-items-center shrink-0 mt-0.5">
                <X className="w-3.5 h-3.5 text-white" strokeWidth={3} />
              </div>
              <div>
                <div className="font-mono-data text-[11px] uppercase tracking-[0.2em] text-neutral-500">
                  Estado B
                </div>
                <div className="font-display text-xl tracking-tight mt-1">
                  Pendiente
                </div>
                <p className="text-sm text-neutral-600 mt-2">
                  El módulo aún no está implementado para esta planta.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer
          className="border-t border-neutral-200"
          data-testid="site-footer"
        >
          <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="font-mono-data text-xs uppercase tracking-[0.2em] text-neutral-500">
              {cfg.footer}
            </div>
          </div>
        </footer>
      </div>
    </TooltipProvider>
  );
}
